import React from "react";
import { FormInstance, InputNumber, Radio, Space, Typography } from "antd";
import { TableProps } from "antd/lib/table";
import { useEditServiceContext } from "components/ratecard/contextProviders";
import { EditableTableProps } from "components/ratecard/_common/EditableTable/EditableTable";
import { formatPrice } from "helperFunctions";
import { ConsumableMaterial, ConsumableMaterialFee } from "services/BillingEngineService/interfaces";
import EditableTable from "components/ratecard/_common/EditableTable";
import { useSuppliesContext } from "components/ratecard/contextProviders/SuppliesContextProvider";
import { random } from "lodash";

type UnitsOption = "percentage" | "fixed";

const optionUnitsSymbolMap: Record<UnitsOption, string> = {
  percentage: "%",
  fixed: "$",
};

const EditableSupplyTable: <Supply>(props: EditableTableProps<any>, showActionColumn: false) => JSX.Element =
  EditableTable;

interface SuppliesTableProps {
  form: FormInstance;
}

const SuppliesTable: React.FC<TableProps<ConsumableMaterial> & SuppliesTableProps> = ({ dataSource = [], form }) => {
  const consumableMaterials: ConsumableMaterial[] = [...dataSource];

  const { status } = useSuppliesContext();
  const { allowEditing } = useEditServiceContext();
  const [consumableMaterialsState, setConsumableMaterialsState] =
    React.useState<ConsumableMaterial[]>(consumableMaterials);

  React.useEffect(() => {
    const consumableMaterials: ConsumableMaterial[] = [...dataSource];
    setConsumableMaterialsState(consumableMaterials);
  }, [dataSource]);

  const renderSkuColumn = (value: string) => <Typography.Text>{value}</Typography.Text>;

  const updateUnits = (consumableMaterialsState: ConsumableMaterial[]) => {
    setConsumableMaterialsState(consumableMaterialsState);

    const supplies = form.getFieldValue("supplies") || [];

    const unitsFiltered = consumableMaterialsState
      .filter((u) => u.fee?.value !== null && u.fee?.value !== undefined)
      .map((cms) => cms.fee);

    const defaultConsumableMaterialFee = supplies.find((cmf: ConsumableMaterialFee) => cmf.materialId === "-1");

    if (defaultConsumableMaterialFee) {
      unitsFiltered.push(defaultConsumableMaterialFee);
    }

    form.setFieldsValue({
      supplies: unitsFiltered,
    });
  };

  const onUnitsChange = (id: string, unitValue: UnitsOption) => {
    const consumableMaterial = consumableMaterialsState.find((cms) => cms.id === id);
    if (consumableMaterial && consumableMaterial.fee) {
      consumableMaterial.fee.unit = unitValue;
    }
    updateUnits(Array.from(consumableMaterialsState));
  };

  const onValueChange = (id: string, newValue: number) => {
    const consumableMaterial = consumableMaterialsState.find((cms) => cms.id === id);
    if (consumableMaterial && consumableMaterial.fee) {
      consumableMaterial.fee.value = newValue;
    }
    updateUnits(Array.from(consumableMaterialsState));
  };

  const getUnitTypeById = (id: string) => {
    return consumableMaterialsState.find((cms) => cms.id === id)?.fee?.unit || "percentage";
  };

  const getValueById = (id: string) => {
    return consumableMaterialsState.find((cms) => cms.id === id)?.fee?.value;
  };

  const columns = [
    {
      title: "SKU",
      dataIndex: "code",
      key: "code",
      width: "20%",
      render: renderSkuColumn,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "20%",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      inputType: "number",
      width: "20%",
      render: (value: number) => {
        if (value === undefined) {
          return formatPrice(random(true));
        }

        return formatPrice(value);
      },
    },
    {
      dataIndex: "id",
      key: "id",
      width: "20%",
      // eslint-disable-next-line react/display-name
      render: (id: string) => (
        <Space>
          {consumableMaterialsState.length > 0 && getUnitTypeById(id) === "percentage" && (
            <InputNumber
              value={getValueById(id)}
              onChange={(value) => onValueChange(id, value)}
              style={{ width: 100 }}
              disabled={!allowEditing}
              min={0}
              max={100}
            />
          )}
          {consumableMaterialsState.length > 0 && getUnitTypeById(id) === "fixed" && (
            <InputNumber
              value={getValueById(id)}
              onChange={(value) => onValueChange(id, value)}
              style={{ width: 100 }}
              precision={2}
              step={0.1}
              disabled={!allowEditing}
              min={0}
            />
          )}
          <Radio.Group
            value={getUnitTypeById(id)}
            buttonStyle="solid"
            onChange={(e) => onUnitsChange(id, e.target.value)}
            disabled={!allowEditing}
          >
            <Radio.Button value="percentage">{optionUnitsSymbolMap["percentage"]}</Radio.Button>
            <Radio.Button value="fixed">{optionUnitsSymbolMap["fixed"]}</Radio.Button>
          </Radio.Group>
        </Space>
      ),
    },
  ];

  return (
    <EditableSupplyTable
      dataSource={dataSource}
      columns={columns}
      editMode={allowEditing}
      loading={status === "loading"}
    />
  );
};

export default SuppliesTable;
